// ============================================================
//  Rechenregeln R1–R4 (Umsetzung §3.2) + Verhältnistabelle + Formatierung.
//  Die vier Kommentare stehen bewusst so im Code.
// ============================================================

import type { LicenseClass } from "./types";

// R1  FUE summiert über PERSONEN, nicht über Systeme oder Konten.
//     Eine Person mit Konten in 40 Mandanten ist EINE Person.
// R2  Die Klasse einer Person ist das MAXIMUM aller Klassen, die irgendeine
//     ihrer Berechtigungen irgendwo erzwingt.
// R3  Ein Hebel senkt eine Person nur, wenn kein ZWEITER Treiber sie oben hält.
//     ⇒ effektiv neu rechnen, niemals Rollen zählen.
// R4  Der Warenkorb simuliert einen ZIELZUSTAND. Zwei Maßnahmen auf dieselbe
//     Person zählen einmal.

export type RatioTable = Record<LicenseClass, number>;

/** Verhältnistabelle — Vertragsparameter, NICHT Konstanten (§3.1). */
export const DEFAULT_RATIO: RatioTable = {
  Developer: 2,
  Advanced: 1,
  Core: 1 / 5,
  SelfService: 1 / 30,
  NotRelevant: 0,
};

/** Preis je FUE — Parameter, kein Literal im Rechenweg (I8). */
export const DEFAULT_PRICE_PER_FUE = 600; // €/Jahr

export const CLASS_ORDER: LicenseClass[] = [
  "NotRelevant",
  "SelfService",
  "Core",
  "Advanced",
  "Developer",
];

export const CLASS_LABEL: Record<LicenseClass, string> = {
  Developer: "Developer",
  Advanced: "Advanced Use",
  Core: "Core Use",
  SelfService: "Self-Service",
  NotRelevant: "nicht relevant",
};

export function classRank(c: LicenseClass): number {
  return CLASS_ORDER.indexOf(c);
}

/** R2: höchste Klasse gewinnt. */
export function maxClass(classes: LicenseClass[]): LicenseClass {
  if (classes.length === 0) return "NotRelevant";
  return classes.reduce((a, b) => (classRank(b) > classRank(a) ? b : a));
}

export function weight(c: LicenseClass, ratio: RatioTable = DEFAULT_RATIO): number {
  return ratio[c] ?? 0;
}

// ------------------------------------------------------------ deterministische Zufallsquelle
/** mulberry32 — deterministischer PRNG aus einem 32-Bit-Seed. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ------------------------------------------------------------ Formatierung (deutsch)
const nf0 = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });
const nf2 = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

export const formatNum = (n: number) => nf0.format(Math.round(n));
export const formatFue = (n: number) => `${nf0.format(Math.round(n))} FUE`;
export const formatFue1 = (n: number) => `${nf1.format(n)} FUE`;
// Gewicht je Lizenzklasse: kleine Werte (Self-Service 0,0333) dürfen nicht auf „0" runden.
const nfW = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 4 });
export const formatWeight = (n: number) => (n !== 0 && Math.abs(n) < 0.1 ? nfW.format(n) : nf1.format(n));

/**
 * Genaues Gewicht für Rechenwege. `formatWeight` rundet auf eine Nachkommastelle —
 * damit wurde aus der Differenz 1 − 0,0333 = 0,9667 die Anzeige „1", und die Zeile
 * „60 × 1 = 58" widersprach sich selbst. In einer offengelegten Rechnung darf nicht
 * gerundet werden, bevor multipliziert wird.
 */
const nfExakt = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 4 });
export const formatWeightExact = (n: number) => nfExakt.format(n);
export const formatEuro = (n: number) => `${nf0.format(Math.round(n))} €`;
export const formatEuroMio = (n: number) => `${nf2.format(n / 1_000_000)} Mio €`;
/** „1 Tag" / „n Tage" — Singular nur bei genau 1. */
export const formatTage = (n: number) => (Math.round(n) === 1 ? "1 Tag" : `${nf0.format(Math.round(n))} Tage`);
export function formatPct(n: number, d = 0): string {
  return `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n)} %`;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function formatDateDe(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
}

/** M3: rechnet gegen den ECHTEN heutigen Tag, nicht gegen ein festes Datum. */
export function daysUntil(iso?: string, from: string = todayIso()): number | null {
  if (!iso) return null;
  const a = new Date(iso + "T00:00:00").getTime();
  const b = new Date(from + "T00:00:00").getTime();
  return Math.round((a - b) / 86_400_000);
}
